import React, { Component } from 'react'
import { createConsumer } from '@rails/actioncable'
import { connect } from 'react-redux'
import {
  roomCreated,
  visitEnded,
  setUserTrackDetails,
  visitUnconnected,
  setWebSocketConnected,
  extendVisit,
  setVisitStatus,
  visitSuspended,
  visitResumed,
  breakInWarningReceived
} from 'actions/visits'
import { VISIT_SUSPENDED_STATUS } from 'constants/visitStatuses'
import {
  MISSED,
  DECLINED,
  ERROR
} from 'constants/unconnectedVisitReasons'
import { noticeError, WebsocketError } from 'utils/newrelic'

export class ActionCableContainer extends Component {
  constructor(props) {
    super(props)
    this.connected = this.connected.bind(this)
    this.received = this.received.bind(this)
    this.disconnected = this.disconnected.bind(this)
  }

  componentDidMount() {
    if (this.props.userToken) {
      this.connect(this.props.userToken)
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.userToken && this.props.userToken) {
      this.connect(this.props.userToken)
    }
  }

  connect(userToken) {
    this.cable = createConsumer(`${process.env.GVS_WEBSOCKET_PROTOCOL}://${new URL(process.env.GVS_URL).host}/cable`)
    this.cable.addSubProtocol(userToken)
    this.cable.subscriptions.create({channel: this.props.channelName}, {
      connected: this.connected,
      disconnected: this.disconnected,
      received: this.received
    })
  }

  received(message) {
    console.log('Received message: ', message)
    if (this.props.visitUnconnected) {
      console.log('Ignoring message because visit is unconnected')
      return
    }
    switch (message.action) {
      case 'twilio_room_create':
        // added if statement according to https://jira.gtl.net/browse/VIDEO-5135?focusedCommentId=660679&page=com.atlassian.jira.plugin.system.issuetabpanels:comment-tabpanel#comment-660679
        var webrtc_token = message.data.webrtc_token || { value: message.data.twilio_token, provider: 'twilio' }
        if (webrtc_token && !webrtc_token.value) break
        this.props.roomCreated(
          message.data.room_uid,
          message.data.participant_ids,
          message.data.server_timestamp,
          webrtc_token
        )
        break
      case 'twilio_room_ended':
        const {
          total_minutes,
          billing: {
            amount_charged,
            coupon_minutes
          } = {}
        } = message.data
        this.props.visitEnded(total_minutes, amount_charged, coupon_minutes)
        break
      case 'sound_on':
        this.props.setUserTrackDetails({
          userId: message.data.user_id,
          audioDisabled: false,
          audioDisabledReason: null
        })
        break
      case 'sound_off':
        this.props.setUserTrackDetails({
          userId: message.data.user_id,
          audioDisabled: true,
          audioDisabledReason: message.data.reason
        })
        break
      case 'video_on':
        this.props.setUserTrackDetails({
          userId: message.data.user_id,
          videoDisabled: false,
          videoDisabledReason: null
        })
        break
      case 'video_off':
        this.props.setUserTrackDetails({
          userId: message.data.user_id,
          videoDisabled: true,
          videoDisabledReason: message.data.reason
        })
        break
      case 'visit_declined':
        this.props.unconnectVisit(DECLINED)
        break
      case 'visit_missed':
        this.props.unconnectVisit(MISSED)
        break
      case 'visit_failed':
        this.props.unconnectVisit(ERROR, message.data.error_message)
        noticeError(new Error('WebSocket messageReceived: "visit_failed"'), {
          errorMessage: message.data.error_message
        })
        break
      case 'visit_suspended':
        this.props.visitSuspended()
        this.props.setVisitStatus(VISIT_SUSPENDED_STATUS)
        break
      case 'visit_resumed':
        this.props.visitResumed()
        this.props.setVisitStatus(null)
        break
      case 'visit_extended':
        this.props.extendVisit(message.data.scheduled_end_time)
        break
      case 'visit_checkin_redirect':
        window.location.replace(message.data.redirect_url)
        break
      case 'break_in_warning':
        const lang = Object.keys(message.data.message)[0]
        this.props.breakInWarningReceived(message.data.message[lang].message)
        break
      default:
        console.warn('Unknown action.')
    }
  }

  connected() {
    console.log('Connected to WebSocket.')
    this.props.setWebSocketConnected()
  }

  disconnected()  {
    const message = 'Disconnected from WebSocket.'
    noticeError(new WebsocketError(message), {
      appointmentId: this.props.appointmentId,
      conferenceId: this.props.conferenceId
    })
    console.warn(message)
  }

  render() {
    return false
  }
}

function mapStateToProps (state) {
  return {
    userToken: state.visit.userToken,
    appointmentId: state.visit.appointmentId,
    conferenceId: state.visit.conferenceId,
    visitUnconnected: state.visit.visitUnconnected
  }
}

function mapDispatchToProps (dispatch) {
  return {
    roomCreated: (name, token, participantIds, serverTimestamp) => dispatch(roomCreated(name, token, participantIds, serverTimestamp)),
    visitEnded: (totalMinutes, amountCharged, couponMinutes) => dispatch(visitEnded(totalMinutes, amountCharged, couponMinutes)),
    setUserTrackDetails: (details) => dispatch(setUserTrackDetails(details)),
    unconnectVisit: (reason, errorCode) => dispatch(visitUnconnected(reason, errorCode)),
    setVisitStatus: (status) => dispatch(setVisitStatus(status)),
    setWebSocketConnected: () => dispatch(setWebSocketConnected()),
    visitSuspended: () => dispatch(visitSuspended()),
    visitResumed: () => dispatch(visitResumed()),
    extendVisit: (scheduledEndTime) => dispatch(extendVisit(scheduledEndTime)),
    breakInWarningReceived: (message) => dispatch(breakInWarningReceived(message))
  }
}

function mergeProps (stateProps, dispatchProps, ownProps) {
  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    userToken: ownProps.userToken || stateProps.userToken
  }
}

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(ActionCableContainer)
